import { useDataModifications } from 'global/cnr/contexts/useHelpers/useDataModifications';
import { getFirstObject } from 'global/common/convert';
import { gEnums } from 'global/enums/globalEnums';
import { currentHelpers } from 'global/redirection/current';
import { appUserPermissionTypes, dataModificationTypes, itemActionTypes, pageItemActionTypes } from 'global/viewSettings/enums/itemActionTypes';
import _ from 'lodash';
import { useContext, useMemo } from 'react';
import { DataContext } from '../DataContext';
import { ParentContext } from '../ParentContext';
import { _viewStatusTypes, useViewItemStatus } from './useViewItemStatus';
import { useAppUserAccessPermissions } from './useAppUserAccessPermissions';
import { useAppUserAccess } from './useAppUserAccess';

export const useAccessPermissions = (props) => {

  const {
    accessRequests,
    view,
    viewItem,
    viewKey,
  } = props;

  const parentContext = useContext(ParentContext);
  const { states } = parentContext ?? {};
  const { currentSettings_state } = states ?? {};
  const { gvviSettings } = currentSettings_state ?? {};
  const appUserAccess = useAppUserAccess();

  const dataContext = useContext(DataContext);
  const { data_state } = dataContext ?? {};
  const { viewListData } = data_state ?? {};

  const { item: itemData } = viewListData ? getFirstObject(viewListData) : {};

  const viewItemStatus = useViewItemStatus()
  const appUserAccessPermissions = useAppUserAccessPermissions()
  const { appUserDataPermission_allowed } = appUserAccessPermissions ?? {};

  const { accessLevel: accessLevel_appUser, loggedIn } = appUserAccess ?? {};

  const { aps_global, aps_viewItems, aps_appUserSettings } = gvviSettings ?? {};
  const { pageItemActions, appUserPagePermissions, search, maps } = viewItem ?? {};

  const { _dataModificationOpts: _dataModifications } = useDataModifications();

  const actionItems = useMemo(() => {
    const {
      appNotifications,
      appSignIn,
      appUserPermissions,
      dataPermissions,
      tools,
    } = aps_global ?? {};

    const {
      allowNotifications,
      allowDirectToNotificationTargets,
      notificationTargets,
      notificationsAuthLevel,
    } = appNotifications ?? {};

    const {
      allowRating,
      allowedRatingPageItems,
      allowRequestAccess,
      requestAccessCollections,
    } = appUserPermissions ?? {};

    const {
      appUserCollection,
      appUserCollections,
      allowCloning,
    } = aps_appUserSettings ?? {};

    const { allowSignInDirect, directSignInType } = appSignIn ?? {};
    const { showSearch, searchOnPage } = search ?? {};
    const { showGeoMapList } = maps ?? {};
    const { showItemCount } = tools ?? {};

    const { allowItemDataModification, accessLevel: accessLevel_gdo } = dataPermissions ?? {};

    const appSettings_viewItem =
      aps_viewItems && viewItem && aps_viewItems[viewItem.key]
        ? aps_viewItems[viewItem.key]
        : null;

    const { dataModifications: dataModifications_app } = appSettings_viewItem ?? {};
    const dataModifications_c = { ...dataModifications_app, ..._dataModifications };

    const items = {
      appUserDirect: {},
      dataManagement: {},
      dataModification: {},
    };

    checkListType_modification(items.dataModification, dataModificationTypes, dataModifications_c, viewItemStatus, appUserAccess, viewItem, appUserDataPermission_allowed);
    checkListType_modification(items.appUserDirect, pageItemActionTypes, pageItemActions, viewItemStatus, appUserAccess);
    checkListType_global(items.appUserDirect, aps_global, viewItemStatus, appUserAccess, view, viewKey);
    checkListType_modification(items.appUserDirect, appUserPermissionTypes, appUserPagePermissions, viewItemStatus, appUserAccess);

    if (allowNotifications && allowDirectToNotificationTargets && notificationTargets && viewItem && notificationTargets.includes(viewItem.key)) {
      let allowNotif = notificationTargets.includes(viewItem.key);
      if (allowNotif) {
        allowNotif = notificationsAuthLevel
          ? accessLevel_appUser >= notificationsAuthLevel
          : appUserAccess.isAdminOrSuper;
        if (allowNotif) {
          items.appUserDirect[itemActionTypes.notifications] = { allow: true };
        }
      }
    }

    const _isAppUserCollection =
      viewItem &&
      ((viewItem.key === appUserCollection) ||
        (appUserCollections && appUserCollections.includes(viewItem.key)));

    if (viewItemStatus.isViewItemSolo && allowCloning && _isAppUserCollection && appUserAccess?.isAdminOrSuper) {
      items.appUserDirect[itemActionTypes.signInAs] = { allow: true };
    }

    addMenuItem_signInDirect(items.appUserDirect, allowSignInDirect, directSignInType, appUserCollection, view, viewKey, viewItem, loggedIn);

    if (allowItemDataModification && accessLevel_appUser >= accessLevel_gdo) {
      if (!viewItemStatus.isViewItemSolo && viewItem.key === view)
        items.appUserDirect[itemActionTypes.add] = { allow: true };
      if (viewItemStatus.isViewItemSolo && viewItem.key === view)
        items.appUserDirect[itemActionTypes.edit] = { allow: true };
      if (viewItemStatus.isViewItemSolo && _isAppUserCollection)
        items.appUserDirect[itemActionTypes.appUserInfo] = { allow: true };
    }

    if (showSearch && !searchOnPage && !viewItemStatus.isViewItemSolo)
      items.appUserDirect[itemActionTypes.search] = { allow: true };

    if (showGeoMapList)
      items.appUserDirect[itemActionTypes.geoMapList] = { allow: true };

    if (showItemCount)
      items.appUserDirect[itemActionTypes.showItemCount] = { allow: true };

    if (allowRating && allowedRatingPageItems && viewKey && allowedRatingPageItems.includes(view))
      items.appUserDirect[appUserPermissionTypes.rating] = { allow: true };

    if (allowRequestAccess && requestAccessCollections && viewKey && requestAccessCollections.includes(view))
      items.appUserDirect[appUserPermissionTypes.requestAccess] = { allow: true };

    addMenuItem_accessRequests(items.appUserDirect, accessRequests, viewItemStatus, itemData, viewItem);

    return items;
  }, [
    aps_global,
    aps_viewItems,
    aps_appUserSettings,
    appUserAccess,
    accessLevel_appUser,
    _dataModifications,
    viewItemStatus,
    view,
    viewItem,
    viewKey,
    loggedIn,
    itemData,
    accessRequests,
  ]);

  return actionItems

};

const addMenuItem_accessRequests = (dms, accessRequests, viewItemStatus, itemData, viewItem) => {
  const { isViewItemSolo } = viewItemStatus ?? {}
  if (accessRequests && isViewItemSolo && itemData) {
    Object.keys(accessRequests).forEach(key => {
      const accessRequest = accessRequests[key]
      delete accessRequest.id
      Object.keys(accessRequest).forEach(keyy => {
        const ar = accessRequest[keyy]
        if (ar.pathName) {
          const alp = currentHelpers.getLastPathView(ar.pathName)
          const { path, key } = alp ?? {}
          if (viewItem.key === path && itemData.id === key) {
            dms[itemActionTypes.viewAccessRequests] = { allow: true }
          }
        }
      })
    })
  }
}

const addMenuItem_signInDirect = (dms, allowSignInDirect, directSignInType, appUserCollection, view, viewKey, viewItem, loggedIn) => {
  if (allowSignInDirect && view !== 'landing' && viewKey && viewItem && viewItem.key && (viewItem.key.toLowerCase().indexOf(appUserCollection.toLowerCase()) >= 0)) {
    if (!loggedIn) {
      switch (directSignInType) {
        case gEnums.directSignInTypes.email:
          dms[itemActionTypes.signInDirectEmail] = { allow: true }
          break;
        case gEnums.directSignInTypes.phoneNumber:
          dms[itemActionTypes.signInDirectPhone] = { allow: true }
          break;
        default:
        // nothing
      }
    }
  }
}

const checkListType_modification = (listTypes, listType, actions, viewItemStatus, appUserAccess, viewItem, appUserDataPermission_allowed) => {
  _.forEach(listType, (type) => {
    const { allow, actionCaption, subActionTypes } = validateDataModification_appUser(type, appUserAccess, getPermissions(actions, type, viewItemStatus, appUserAccess), appUserDataPermission_allowed)
    if (allow) {
      listTypes[type] = { allow: true, actionCaption, subActionTypes }
    }
  })
}

const checkListType_global = (listTypes, aps_global, viewItemStatus, appUserAccess, view, viewKey) => {

  const { accessLevel } = appUserAccess ?? {}

  const {
    appQuestionsAndAnswers,
    appRating,
    appVideoConferencing,
  } = aps_global ?? {}

  const { allowQuestionsAndAnswers, qAndAAuthLevel, questionsAndAnswersCollections } = appQuestionsAndAnswers ?? {}
  const { allowRating, ratingAuthLevel, allowedRatingPageItems } = appRating ?? {}
  const { allowVideoConferencing, videoConferencingAuthLevel, videoConferencingCollections } = appVideoConferencing ?? {}

  if (allowQuestionsAndAnswers &&
    accessLevel >= qAndAAuthLevel &&
    questionsAndAnswersCollections &&
    questionsAndAnswersCollections.includes(view) &&
    viewKey) {
    listTypes['qAndAManager'] = { allow: true }
  }

  if (allowRating &&
    accessLevel >= ratingAuthLevel &&
    allowedRatingPageItems &&
    allowedRatingPageItems.includes(view) &&
    viewKey) {
    listTypes['ratingManager'] = { allow: true }
  }

  if (allowVideoConferencing &&
    accessLevel >= videoConferencingAuthLevel &&
    videoConferencingCollections &&
    videoConferencingCollections.includes(view) &&
    viewKey
  ) {
    listTypes['videoConferencing'] = { allow: true }
  }

}

const validateDataModification_appUser = (modType, appUserAccess, permissionProps) => {

  const { allowAction, accessLevel: accessLevel_item, actionCaption, subActionTypes } = permissionProps ?? {}
  const { accessLevel: accessLevel_appUser } = appUserAccess ?? {}

  const allows = {
    allow: false,
    actionCaption
  }

  // if the appUser is a admin, allow the action
  if (accessLevel_appUser && (accessLevel_appUser >= gEnums.accessLevels.admin.value)) {
    allows.allow = allowAction
  } else {
    if (accessLevel_item) {
      switch (accessLevel_item) {
        // allow if accessLevel is public
        case gEnums.accessLevels.public.value:
          allows.allow = true
          break;
        default:
          if (allowAction && accessLevel_appUser >= accessLevel_item) {
            allows.allow = true
          }
      }
    } else {
      switch (modType) {
        case itemActionTypes.emailAuth:
          if (allowAction && !appUserAccess.userData) {
            allows.allow = true
            // return { allow: true, actionCaption }
          }
          break;
        default:
        // nothing
      }
    }
  }

  if (subActionTypes) {
    allows.subActionTypes = subActionTypes
  }

  return { ...allows }
}

const getPermissions = (actions, modType, viewItemStatus, appUserAccess) => {

  const action = actions ? actions[modType] : {}
  const { allowAction } = action ?? {}
  let allow = allowAction

  const { viewStatusType, isViewItemSolo, isViewItemList } = viewItemStatus ?? {}

  if (!action) {
    allow = false
  } else {
    switch (viewStatusType) {
      case _viewStatusTypes.solo:
        switch (modType) {
          case itemActionTypes.edit:
          case itemActionTypes.imageUpload:
          case itemActionTypes.rating:
          case itemActionTypes.requestAccess:
            allow = true
            break;
          default:
          // nothing
        }
        break;

      case _viewStatusTypes.list:
        switch (modType) {
          case itemActionTypes.add:
          case itemActionTypes.status:
            allow = true
          default:
          // nothing
        }
        break;

      case _viewStatusTypes.subList:
        switch (modType) {
          case itemActionTypes.dataLinking:
          case itemActionTypes.filters:
            allow = true
          default:
          // nothing
        }
    }

  }

  switch (modType) {
    case itemActionTypes.edit:
      if (appUserAccess >= gEnums.accessLevels.admin.value) {
        return action
      }
      break;
    default:
    // nothing
  }

  return allow ? action : {}
}
